import { Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, ShieldX, SearchCheck, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const dt = (s) => (s ? new Date(s).toLocaleString("fr-FR") : "—");

function Result({ r }) {
  if (!r.found) return (
    <div className="rounded-2xl border-2 border-rose-300 bg-rose-50 p-5 text-center" data-testid="verify-notfound">
      <ShieldX className="h-12 w-12 text-rose-600 mx-auto" /><p className="font-head text-lg font-bold mt-2">Document inconnu</p>
      <p className="text-sm text-slate-600">Aucun courrier signé ne correspond au code <b className="font-mono">{r.code}</b>. Ce document n'est pas authentique ou le code est erroné.</p>
    </div>
  );
  const ok = r.statut === "valide";
  return (
    <div className={`rounded-2xl border-2 p-5 ${ok ? "border-emerald-400 bg-emerald-50" : "border-orange-300 bg-orange-50"}`} data-testid="verify-result">
      <div className="text-center">
        {ok ? <ShieldCheck className="h-12 w-12 text-emerald-600 mx-auto" /> : <ShieldX className="h-12 w-12 text-orange-600 mx-auto" />}
        <p className="font-head text-lg font-bold mt-2" data-testid="verify-status">{ok ? "Document authentique et valide" : "Signature annulée"}</p>
        <p className="text-xs text-slate-600">{ok ? `Ce courrier a bien été émis et signé électroniquement par ${r.company}.` : `Ce courrier a été émis par ${r.company} mais sa signature a été annulée${r.annule_le ? ` le ${dt(r.annule_le)}` : ""}.`}</p>
      </div>
      <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm bg-white rounded-xl p-3">
        {[["Référence", r.reference], ["Date", r.date], ["Objet", r.objet], ["Destinataire", r.destinataire], ["Signataire", r.signataire], ["Signé le", dt(r.signe_le)], ["Code", r.code]].map(([k, v]) => (
          <Fragment key={k}><dt className="text-slate-500">{k}</dt><dd className="font-medium break-words" data-testid={`verify-${k.toLowerCase().replace(/\W/g, "")}`}>{v || "—"}</dd></Fragment>
        ))}
      </dl>
    </div>
  );
}

export default function VerifyLetter() {
  const { code } = useParams();
  const nav = useNavigate();
  const [input, setInput] = useState(code || "");
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!code) return;
    setBusy(true); setErr(""); setR(null);
    axios.get(`${API}/public/verify/${encodeURIComponent(code)}`).then((x) => setR(x.data)).catch((e) => setErr(e.response?.data?.detail || "Vérification impossible")).finally(() => setBusy(false));
  }, [code]);
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10" data-testid="verify-page">
      <div className="max-w-lg mx-auto">
        <p className="text-[11px] font-mono uppercase tracking-widest text-brand-700 text-center">OMNIBAT ONE</p>
        <h1 className="font-head text-2xl sm:text-3xl font-bold text-center mb-6">Vérification de document</h1>
        <form className="flex gap-2 mb-5" onSubmit={(e) => { e.preventDefault(); if (input.trim()) nav(`/verifier/${input.trim()}`); }}>
          <input value={input} onChange={(e) => setInput(e.target.value.toUpperCase())} placeholder="Code : XXXX-XXXX-XXXX" className="flex-1 h-11 rounded-lg border px-3 font-mono" data-testid="verify-input" />
          <button className="h-11 px-4 rounded-lg bg-brand-600 text-white font-semibold flex items-center" data-testid="verify-submit"><SearchCheck className="h-4 w-4 mr-1" />Vérifier</button>
        </form>
        {busy && <p className="text-center text-sm"><Loader2 className="inline h-4 w-4 animate-spin mr-1" />Vérification…</p>}
        {err && <p className="text-center text-sm text-rose-600" data-testid="verify-error">{err}</p>}
        {r && <Result r={r} />}
        {!code && <p className="text-sm text-slate-500 text-center">Saisissez le code imprimé sous la signature du courrier ou scannez son QR code.</p>}
        <p className="text-[11px] text-slate-400 text-center mt-6">Le contenu du courrier n'est jamais affiché sur cette page.</p>
      </div>
    </div>
  );
}
